/**
 * This type ensures that a valid css variable name will be passed, followed by a default value
 */
export type CssVariableType<T> = `var(${string & keyof T}, ${string})`;

/**
 * The following interfaces and objects supply the architecture needed to use CSS custom properties within durations
 * and account for the prefers-reduced-motion media query - MUI components do not currently support custom properties,
 * but this section should be revisited when possible
 */

// export interface DurationTokens {
//   '--duration-shortest': string;
//   '--duration-shorter': string;
//   '--duration-short': string;
//   '--duration-standard': string;
//   '--duration-complex': string;
//   '--duration-entering-screen': string;
//   '--duration-leaving-screen': string;
// }

// export const durationTokens: DurationTokens = {
//   '--duration-shortest': '150ms',
//   '--duration-shorter': '200ms',
//   '--duration-short': '250ms',
//   '--duration-standard': '300ms',
//   '--duration-complex': '375ms',
//   '--duration-entering-screen': '225ms',
//   '--duration-leaving-screen': '195ms',
// };

// export const durationTokensReducedMotion: DurationTokens = {
//   '--duration-shortest': '0ms',
//   '--duration-shorter': '0ms',
//   '--duration-short': '0ms',
//   '--duration-standard': '0ms',
//   '--duration-complex': '0ms',
//   '--duration-entering-screen': '0ms',
//   '--duration-leaving-screen': '0ms',
// };

export interface OmakaseColorTokens {
  '--bg-default': string;
  '--bg-lighter': string;
  '--bg-light': string;
  '--bg-dark': string;
  '--bg-darker': string;
  '--bg-contrast': string;
  '--bg-overlay': string;
  '--bg-primary-lighter': string;
  '--bg-primary-light': string;
  '--bg-primary-base': string;
  '--bg-primary-dark': string;
  '--bg-success-lighter': string;
  '--bg-success-base': string;
  '--bg-warning-lighter': string;
  '--bg-warning-base': string;
  '--bg-danger-lighter': string;
  '--bg-danger-base': string;

  '--border-default': string;
  '--border-dark': string;
  '--border-contrast': string;
  '--border-primary-light': string;
  '--border-primary-base': string;
  '--border-primary-dark': string;
  '--border-success-light': string;
  '--border-success-base': string;
  '--border-warning-light': string;
  '--border-warning-base': string;
  '--border-danger-light': string;
  '--border-danger-base': string;

  '--fg-default': string;
  '--fg-subtle': string;
  '--fg-disabled': string;
  '--fg-contrast': string;
  '--fg-primary-dark': string;
  '--fg-primary-base': string;
  '--fg-danger-base': string;
  '--fg-success-base': string;
  '--fg-warning-base': string;

  '--decorative-01': string;
  '--decorative-02': string;
  '--decorative-03': string;
  '--decorative-04': string;
  '--decorative-05': string;
  '--decorative-06': string;
  '--decorative-07': string;
  '--decorative-08': string;
  '--decorative-09': string;
  '--decorative-10': string;
  '--decorative-11': string;
  '--decorative-12': string;
  '--decorative-13': string;
  '--decorative-14': string;
  '--decorative-15': string;
  '--decorative-16': string;
  '--decorative-17': string;
  '--decorative-18': string;
  '--decorative-19': string;
}

/**
 * These variables are intended to exactly cover, 1:1, variables that are used in definitions in omakaseColors.ts (const omakaseLightColors)
 */
export const omakaseColorTokensLight: OmakaseColorTokens = {
  '--bg-default': '#fff',
  '--bg-lighter': '#f9f9f9',
  '--bg-light': '#f6f6f7',
  '--bg-dark': '#484848',
  '--bg-darker': '#2b2b2b',
  '--bg-contrast': '#000000',
  '--bg-overlay': 'rgba(0, 0, 0, 0.5)',
  '--bg-primary-lighter': '#f1faff',
  '--bg-primary-light': '#d0e2ff',
  '--bg-primary-base': '#344ef4',
  '--bg-primary-dark': '#2a2ae9',
  '--bg-success-lighter': '#f1f8f5',
  '--bg-success-base': '#008060',
  '--bg-warning-base': '#916a00',
  '--bg-warning-lighter': '#fff5ea',
  '--bg-danger-lighter': '#fff4f4',
  '--bg-danger-base': '#da1e28',

  '--border-default': '#e7e7e7',
  '--border-dark': '#2b2b2b',
  '--border-contrast': '#ffffff',
  '--border-primary-light': '#abddff',
  '--border-primary-base': '#344ef4',
  '--border-primary-dark': '#2a2ae9',
  '--border-success-light': '#aee9d1',
  '--border-success-base': '#008060',
  '--border-warning-light': '#ffd79d',
  '--border-warning-base': '#916a00',
  '--border-danger-light': '#ffbbbf',
  '--border-danger-base': '#da1e28',

  '--fg-default': '#2b2b2b',
  '--fg-subtle': '#6f6f6f',
  '--fg-disabled': '#8c9196',
  '--fg-contrast': '#fff',
  '--fg-primary-base': '#344ef4',
  '--fg-primary-dark': '#2a2ae9',
  '--fg-danger-base': '#da1e28',
  '--fg-success-base': '#008060',
  '--fg-warning-base': '#916a00',

  '--decorative-01': '#ee6b62',
  '--decorative-02': '#edb815',
  '--decorative-03': '#af6acd',
  '--decorative-04': '#ff4b4b',
  '--decorative-05': '#ffd53e',
  '--decorative-06': '#01ad9d',
  '--decorative-07': '#e17474',
  '--decorative-08': '#bd2333',
  '--decorative-09': '#77624e',
  '--decorative-10': '#d684d8',
  '--decorative-11': '#a8535d',
  '--decorative-12': '#c69928',
  '--decorative-13': '#8e44ad',
  '--decorative-14': '#eb8a54',
  '--decorative-15': '#87bb6e',
  '--decorative-16': '#4368c8',
  '--decorative-17': '#3f90cb',
  '--decorative-18': '#7b7b8b',
  '--decorative-19': '#fabf37',
};

export const omakaseColorTokensHighContrast: OmakaseColorTokens = {
  '--bg-default': '#000',
  '--bg-lighter': '#212121',
  '--bg-light': '#1e1e1e',
  '--bg-dark': '#000',
  '--bg-darker': '#212121',
  '--bg-contrast': '#212121',
  '--bg-overlay': 'rgba(0, 0, 0, 0.5)',
  '--bg-primary-lighter': '#000',
  '--bg-primary-light': '#000',
  '--bg-primary-base': '#4272FD',
  '--bg-primary-dark': '#78A9FF',
  '--bg-success-lighter': '#295c40',
  '--bg-success-base': '#8acdab',
  '--bg-warning-base': '#edb905',
  '--bg-warning-lighter': '#955f0e',
  '--bg-danger-lighter': '#842626',
  '--bg-danger-base': '#d14c4c',

  '--border-default': '#313131',
  '--border-dark': '#000',
  '--border-contrast': '#000',
  '--border-primary-light': '#4272FD',
  '--border-primary-base': '#4272FD',
  '--border-primary-dark': '#78A9FF',
  '--border-success-light': '#295c40',
  '--border-success-base': '#d14c4c',
  '--border-warning-light': '#955f0e',
  '--border-warning-base': '#edb905',
  '--border-danger-light': '#842626',
  '--border-danger-base': '#d14c4c',

  '--fg-default': '#f6f6f7',
  '--fg-subtle': '#fafafa',
  '--fg-disabled': '#a5a5a5',
  '--fg-contrast': '#2b2b2b',
  '--fg-primary-base': '#f6f6f7',
  '--fg-primary-dark': '#4272FD',
  '--fg-danger-base': '#da1e28',
  '--fg-success-base': '#008060',
  '--fg-warning-base': '#916a00',

  '--decorative-01': '#ee6b62',
  '--decorative-02': '#edb815',
  '--decorative-03': '#af6acd',
  '--decorative-04': '#ff4b4b',
  '--decorative-05': '#ffd53e',
  '--decorative-06': '#01ad9d',
  '--decorative-07': '#e17474',
  '--decorative-08': '#bd2333',
  '--decorative-09': '#77624e',
  '--decorative-10': '#d684d8',
  '--decorative-11': '#a8535d',
  '--decorative-12': '#c69928',
  '--decorative-13': '#8e44ad',
  '--decorative-14': '#eb8a54',
  '--decorative-15': '#87bb6e',
  '--decorative-16': '#4368c8',
  '--decorative-17': '#3f90cb',
  '--decorative-18': '#7b7b8b',
  '--decorative-19': '#fabf37',
};

/**
 * These variables are intended to exactly cover, 1:1, variables that are used in boxShadow definitions in the Theme object (Theme.tsx)
 */
export interface OmakaseBoxShadowTokens {
  '--drop-shadow-01': string;
  '--drop-shadow-none': string;
  '--inner-shadow-01': string;
  '--inner-shadow-02': string;
  '--inner-shadow-03': string;
  '--inner-shadow-04': string;
  '--inner-shadow-05': string;
  '--elevation-01': string;
}

export const omakaseBoxShadowTokensLight: OmakaseBoxShadowTokens = {
  '--drop-shadow-01': '0 1px 0 0 rgba(0, 0, 0, 0.05)',
  '--drop-shadow-none': '0 1px 0 0 rgba(0, 0, 0, 0)',
  '--inner-shadow-01': 'inset 0 2px 0 0 rgba(0, 0, 0, 0.02)',
  '--inner-shadow-02': 'inset 0 2px 0 0 rgba(0, 0, 0, 0.08)',
  '--inner-shadow-03': 'inset 0 -2px 0 0 rgba(0, 0, 0, 0.2)',
  '--inner-shadow-04': 'inset 0 2px 0 0 rgba(0, 0, 0, 0.2)',
  '--inner-shadow-05': 'inset 0 -1px 0 0 rgba(0, 0, 0, 0.2)',
  '--elevation-01': '0 2px 8px 0 rgba(0, 0, 0, 0.08)',
};

export interface BoxShadowTokensImpl {
  dropShadow01: CssVariableType<OmakaseBoxShadowTokens>;
  dropShadowNone: CssVariableType<OmakaseBoxShadowTokens>;
  innerShadow01: CssVariableType<OmakaseBoxShadowTokens>;
  innerShadow02: CssVariableType<OmakaseBoxShadowTokens>;
  innerShadow03: CssVariableType<OmakaseBoxShadowTokens>;
  innerShadow04: CssVariableType<OmakaseBoxShadowTokens>;
  innerShadow05: CssVariableType<OmakaseBoxShadowTokens>;
  elevation01: CssVariableType<OmakaseBoxShadowTokens>;
}

/**
 * OmakaseBoxShadowsStringType allows for casting omakaseBoxShadows to string type for integration with MUI component style props
 */
export interface OmakaseBoxShadowsStringType {
  dropShadow01: string;
  dropShadowNone: string;
  innerShadow01: string;
  innerShadow02: string;
  innerShadow03: string;
  innerShadow04: string;
  innerShadow05: string;
  elevation01: string;
}

export const omakaseBoxShadows: OmakaseBoxShadowsStringType = {
  dropShadow01: '0 1px 0 0 rgba(0, 0, 0, 0.05)',
  dropShadowNone: '0 1px 0 0 rgba(0, 0, 0, 0)',
  innerShadow01: 'inset 0 2px 0 0 rgba(0, 0, 0, 0.02)',
  innerShadow02: 'inset 0 2px 0 0 rgba(0, 0, 0, 0.08)',
  innerShadow03: 'inset 0 -2px 0 0 rgba(0, 0, 0, 0.2)',
  innerShadow04: 'inset 0 2px 0 0 rgba(0, 0, 0, 0.2))',
  innerShadow05: 'inset 0 -1px 0 0 rgba(0, 0, 0, 0.2)',
  elevation01: '0 2px 8px 0 rgba(0, 0, 0, 0.08)',
};

export interface TypographyTokens {
  '--font-family-sans-regular': string;
  '--font-family-sans-condensed': string;
}

export const omakaseTypographyTokens: TypographyTokens = {
  '--font-family-sans-regular': "'Oracle', 'Helvetica', 'sans-serif'",
  '--font-family-sans-condensed': "'Söhne Schmal', 'Helvetica', 'sans-serif'",
};
